import { AUDIO_SUB_TYPES, BASE_PLAN_SUB_TYPES, BILLING_CYCLE, planTypes, usageTypeMap } from '@/common/constants';
import { hasMultipleSubscriptionPlan } from '@/common/service/multiple-subscriptions';
import { isEmptyVal } from '@/utils';
import { flowTypes, pageConfig } from '@/common/service/page-config';
import { isInrCurrency } from '@/utils/currency';
import { couponFetchStatus } from '@/views/config/coupon/helper/constant';

const isFromCouponURL = () => (
  !isEmptyVal(pageConfig?.marketingCoupon) ||
  !isEmptyVal(pageConfig?.partnerReferral) ||
  !isEmptyVal(pageConfig?.trialCampaignInfo)
)

const isINR = () => isInrCurrency();

const state = {
  // { type: string, mode: 'increase' | 'addnew' }
  msqSupportINR: window.campaignFlags?.msqSupportINR ?? false,
  msqSupportAPM: window.campaignFlags?.msqSupportAPM ?? false,
  autoAddActions: []
}

const mutations = {
  setAutoAddActions(state, actions) {
    state.autoAddActions = actions;
  }
}

const getters = {
  // 1. not from cart builder
  // 2. not in INR OR (in INR and msqSupportINR enabled)
  // 3. if has base plan, it should be in the scope.
  // 4. eligible for msq
  msqSupportINR: (state) => state.msqSupportINR,
  msqSupportAPM: (state) => state.msqSupportAPM,
  isFCCA: (_, getters) => {
    return getters.addressInfo?.country === 'DE' && getters.addressInfo?.usageType === usageTypeMap.personal;
  },
  multipleSubscriptionScope: (_, getters) => {
    const SCOPE_MAP = {
      'ZOPRO': { type: planTypes.ZOOM_MEETING, category: 'pro' },
      'BIZ': { type: planTypes.ZOOM_MEETING, category: 'biz' },
    }
    const SUPPORT_FLOWS = [flowTypes.buyNow, flowTypes.basic2pro, flowTypes.edit, flowTypes.add2cart];

    return (getters.canBuyMsqList || []).map(item => ({
      ...SCOPE_MAP[item],
      flows: SUPPORT_FLOWS,
    })) || [];
  },
  msqFeatureEnabled: (state, getters) => {
    const boughtBasePlan = getters.getBoughtBasePlanInfo?.plans[0];
    let isInScope = true;
    if (boughtBasePlan) {
      isInScope = (
        !boughtBasePlan.isFreeTrial && 
        getters.multipleSubscriptionScope.some(scope => scope.type === boughtBasePlan?.type && scope.category === boughtBasePlan?.category)
      );
    }
    return (
      !isFromCouponURL() && 
      !getters.hasZoomDevice &&
      !getters.referralCode &&
      !(isINR() && !state.msqSupportINR) &&
      isInScope && 
      getters.verifyCouponStatus !== couponFetchStatus.successfully &&
      // support add another plan or already has multiple subscription plan
      (((getters.canBuyMsqList?.length ?? 0) > 0) || getters.basicConfig.multiSubscriptionEnabled) &&
      !getters.isFCCA
    )
  },
  autoAddActions: (state) => state.autoAddActions,
  hasMultipleSubscriptionPlan: (_, getters) => {
    return hasMultipleSubscriptionPlan(getters.getUserCurrentSubscriptions);
  },
  isMultipleSubscriptionAccount: (_, getters) => {
    return getters.hasMultipleSubscriptionPlan || getters.basicConfig.multiSubscriptionEnabled;
  },
  annualBasePlan: (_, getters) => {
    const oldBasePlan = getters.getCurrentBasePlanInfo?.plans[0];
    return getters.planMapFlat
    .filter(p => !getters.boughtPlans.some(bp => bp.plan_code === p.plan_code))
    .find(p => p.type === oldBasePlan?.type && p.category === oldBasePlan?.category && p.billing_cycle === BILLING_CYCLE.ANNUAL);
  },
  supportAutoAddAnotherBasePlan: (_, getters) => {
    if (!getters.msqFeatureEnabled || !getters.annualBasePlan) {
      return false;
    }
    if (![flowTypes.add2cart].includes(pageConfig.type)) {
      return false;
    }
    const boughtBasePlan = getters.getBoughtBasePlanInfo?.plans;
    const hasMeetingPlanInShoppingCart = getters.getSelectedPlans.some(p => (
      p.type === planTypes.ZOOM_MEETING &&
      boughtBasePlan.some(bp => bp.plan_code === p.plan_code)
    ));
    if (hasMeetingPlanInShoppingCart) {
      return false;
    }
    const currentBasePlan = getters.getCurrentBasePlanInfo?.plans[0];
    const hasUnlimitedAudioPlan = getters.boughtPlans.some(p => p.type === planTypes.TOLLFREE && p.category === AUDIO_SUB_TYPES.UNLIMITED);
    if (hasUnlimitedAudioPlan && currentBasePlan?.category === BASE_PLAN_SUB_TYPES.BIZ) {
      return false;
    }
    return getters.multipleSubscriptionScope.some(scope => scope.type === currentBasePlan?.type && scope.category === currentBasePlan.category);
  }
}

const actions = {
  addAnnualBaseForAddonPlan({ getters, commit }) {
    if (!getters.supportAutoAddAnotherBasePlan) return Promise.reject();
    if (!getters.annualBasePlan) return Promise.reject();
    let defaultNumber = 1;

    commit('updateSelectPlan', {
      plan_code: getters.annualBasePlan.plan_code,
      cartInfo: {
        selected_number: defaultNumber,
      },
    })
    commit('setAutoAddActions', [{
      type: getters.annualBasePlan.type,
      mode: 'addnew'
    }]);
    commit('setPreventAutoCollapseOnce', true);
    commit('savePersist')
    return Promise.resolve([getters.annualBasePlan.type]);
  },
  removeAnnualBaseForAddonPlan({ getters, commit }) {
    const autoAddPlans = getters.getSelectedPlans.filter(p => getters.autoAddActions.some(action => action.type === p.type));
    let removedTypes = [];
    if (!autoAddPlans.length) return Promise.reject();
    
    autoAddPlans.forEach(p => {
      if (p?.plan_code) {
        commit('removePlan', p.plan_code);
        removedTypes.push(p.type);
      }
    })
    commit('setAutoAddActions', []);
    commit('savePersist')
    return Promise.resolve(removedTypes);
  }
}

export default {
  state,
  getters,
  mutations,
  actions,
}